import React, { useState, useEffect } from "react";
import { ReactComponent as AddAssetIcon } from "../../../images/add-asset-icon.svg";
import "./addAssetDetails.scss";
import Modal, { handleCheckboxKeyDown } from "../../../common/modal/modal.tsx";
import {
  addAsset,
  editAsset,
  loadAsset,
  loadAssetControllerPort,
  loadAssetControllerType,
  loadAssetApplications,
  loadAssetPOCTypeByApplication,
  searchAssetByName,
  loadAssetTimezone,
  loadAssetMasterDataType,
} from "../assetSlice.ts";
import Dropdown, { DropdownItems } from "../../../common/dropdown/dropdown.tsx";
import AddCustomer from "../../customerDetails/addCustomerDetails/addCustomerDetails.tsx";
import {
  addCustomer,
  loadCustomer,
} from "../../customerDetails/customerSlice.ts";
import { CustomerItem } from "../../customerDetails/customerService.ts";
import SearchDropdown, {
  searchDropdownItem,
} from "../../../common/dropdown/searchDropdown.tsx";
import { AssetItem, MasterDataType } from "../assetService.tsx";
import SuccessfullToast from "../../../common/toast/successfulToast.tsx";
import Tooltip from "../../../common/tooltip/tooltip.tsx";
import ConfirmationDialog from "../../../common/confirmationDialog/confirmationDialog.tsx";
import { ReactComponent as AlertIcon } from "../../../images/alert-circle.svg";
import Loader from "../../../common/loader/loader.tsx";
import { useFormChangeDetection } from "../../../common/formChangeDetection/formChangeDetection.tsx";
import useConfig from "../../../hooks/useConfig.ts";
import { useAppDispatch, useAppSelector } from "../../../hooks/storeHooks.ts";
import useUserAccess from "../../../hooks/useUserAcess.ts";
import ResponseItem from "../../../models/ResponseItem.ts";
import MasterSearchDropdown from "../../../common/dropdown/masterSearchDropdown.tsx";

interface AddAssetProps {
  addAsset: (newAsset: AssetItem) => void;
  editAsset: (updatedAsset: AssetItem) => void;
  assetToEdit?: AssetItem | null;
}

const AddAssetDetails: React.FC<AddAssetProps> = ({ assetToEdit }) => {
  const dispatch = useAppDispatch();
  const searchResults = useAppSelector((state) => state.assets.searchResults);
  const assetSearchLoading = useAppSelector(
    (state) => state.assets.assetSearchLoading
  );
  const assets = useAppSelector((state) => state.assets.asset);
  const customerNames = useAppSelector((state) => state.customers.customer);
  const applications = useAppSelector((state) => state.assets.applications);
  const controllerPorts = useAppSelector((state) => state.assets.port);
  const controllerTypes = useAppSelector((state) => state.assets.type || []);
  const timezones = useAppSelector((state) => state.assets.timezone);
  const fields = useAppSelector((state) => state.assets.fields);
  const businessUnits = useAppSelector((state) => state.assets.businessUnit);
  const assetSaveLoading = useAppSelector(
    (state) => state.assets.assetSaveLoading
  );

  const masterDataLoading = useAppSelector(
    (state) => state.assets.masterDataLoading
  );

  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const { isCloud } = useConfig();
  const { isWellAdmin } = useUserAccess();

  // const isCloud = true;

  const customerItems = customerNames
    .map((customer) => ({
      id: customer.id,
      name: customer.name || "",
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const initialFormData = {
    id: "",
    customerId: isCloud
      ? null
      : customerNames.length > 0
        ? customerNames[0].id
        : null,
    customerName: isCloud
      ? ""
      : customerNames.length > 0
        ? customerNames[0].name
        : "",
    createdOnDate: "",
    lastModifiedDate: "",
    cnX_WellName: "",
    wellname: "",
    cnX_SiteId: null,
    cnX_WellId: null,
    envWellId: null,
    apI_UWI: "",
    pocType: {
      id: "",
      legacyId: "",
      lookupType: "",
      description: "",
    },
    portId: "",
    nodeAddress: "",
    isEnabled: false,
    pumpChecker: {
      identifier: "",
      identifierType: "API",
      field: "",
      area: "",
      businessUnit: "",
      methodOfProduction: "",
      latitude: null,
      longitude: null,
      timezone: "",
    },
    applicationId: "",
    action: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchDropdownItems, setSearchDropdownItems] = useState<
    searchDropdownItem[]
  >([]);
  const [businessUnitDropdownItems, setBusinessUnitDropdownItems] = useState<
    MasterDataType[]
  >([]);
  const [fieldDropdownItems, setFieldDropdownItems] = useState<
    MasterDataType[]
  >([]);
  const [businessUnitInputValue, setBusinessUnitInputValue] = useState<string>("");
  const [fieldInputValue, setFieldInputValue] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [textcolor, setTextColor] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const { setOriginalFormData, hasChanges } =
    useFormChangeDetection<CustomerItem>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (!dataLoaded) {
      if (customerNames.length === 0) {
        dispatch(loadCustomer());
      }
      if (controllerPorts.length === 0) {
        dispatch(loadAssetControllerPort());
      }
      setDataLoaded(true);
    }
  }, [dispatch, dataLoaded, customerNames.length, controllerPorts.length]);

  useEffect(() => {
    if (isCloud !== null) {
      if (isCloud) {
        if (controllerTypes.length === 0) {
          dispatch(loadAssetControllerType());
        }
      } else {
        if (applications.length === 0) {
          dispatch(loadAssetApplications());
        }
        if (timezones.length === 0) {
          dispatch(loadAssetTimezone());
        }
      }
    }
  }, [
    dispatch,
    isCloud,
    controllerTypes.length,
    applications.length,
    timezones.length,
  ]);

  useEffect(() => {
    if (assetToEdit?.applicationId) {
      dispatch(loadAssetPOCTypeByApplication(assetToEdit.applicationId));
    }
    dispatch(loadAssetMasterDataType("BusinessUnit"));
    dispatch(loadAssetMasterDataType("Field"));

  }, [dispatch, assetToEdit?.applicationId]);

  const applicationItems = applications
    .map((method) => ({
      id: method.id,
      name: method.name,
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const timezoneItems = timezones.map((tz) => ({
    id: tz.id || "",
    name: tz.displayName || "",
  }));

  useEffect(() => {
    if (assetToEdit) {
      const matchedApplication = applicationItems.find(
        (method) => method.id === assetToEdit.applicationId
      );

      const matchedTimezone = timezoneItems.find(
        (tz) => tz.id === assetToEdit.timezone
      );

      const matchedBusinessUnit = businessUnits.find(
        (bu) => bu.name === assetToEdit.businessUnit
      );

      const matchedField = fields.find(
        (field) => field.name === assetToEdit.field
      );

      setFormData({
        ...assetToEdit,
        id: assetToEdit.id.toString(),
        wellname: assetToEdit.name,
        pumpChecker: {
          ...assetToEdit.pumpChecker,
          identifier: assetToEdit.apI_UWI || "",
          methodOfProduction: matchedApplication ? matchedApplication.name : "",
          field: matchedField ? matchedField.name : assetToEdit.field || "",
          area: assetToEdit.area || "",
          businessUnit: matchedBusinessUnit ? assetToEdit.businessUnit : "",
          timezone: matchedTimezone ? matchedTimezone.id : "",
        },
        applicationId: assetToEdit?.applicationId || "",
      });
      setFieldInputValue(assetToEdit?.field || "");
      setBusinessUnitInputValue(assetToEdit?.businessUnit || "");
      setOriginalFormData({ ...assetToEdit });
      setIsEditMode(true);
      setSearchDropdownItems([]);
      setBusinessUnitDropdownItems([]);
      setFieldDropdownItems([]);
      openAssetModal();
    }
  }, [assetToEdit, applicationItems, timezoneItems, setOriginalFormData, businessUnits, fields]);

  useEffect(() => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {};

    isValid =
      formData.wellname.trim() !== "" &&
      formData.customerName.trim() !== "" &&
      formData.pocType.id.trim() !== "" &&
      (!isCloud
        ? formData.pumpChecker.identifier !== "" &&
        formData.pumpChecker.methodOfProduction?.trim() !== ""
        : true);

    if (
      formData.wellname &&
      assets.some(
        (asset) =>
          asset.name &&
          asset.name.toLowerCase() === formData.wellname.toLowerCase() &&
          (!isEditMode || asset.id !== formData.id)
      )
    ) {
      newErrors.name = "Asset name already exists";
    }

    if (
      formData.portId &&
      assets.some((asset) => {
        const assetNodeAddress = asset.nodeAddress?.trim().toLowerCase() || "";
        const formNodeAddress =
          formData.nodeAddress?.trim().toLowerCase() || "";

        return (
          asset.portId === formData.portId &&
          assetNodeAddress === formNodeAddress &&
          (!isEditMode || asset.id !== formData.id)
        );
      })
    ) {
      newErrors.portAndAddress = "Address+Port combination already exists";
    }

    // Validation for Field, Area, and Business Unit
    const { field, area, businessUnit } = formData.pumpChecker;
    if (field && (!area || !businessUnit)) {
      isValid = false;
      newErrors.fieldDependency =
        "A Field requires both Area and Business Unit to be provided.";
    }

    if (area && !businessUnit) {
      isValid = false;
      newErrors.areaDependency =
        "An Area requires a Business Unit to be provided.";
    }

    setErrors(newErrors);
    setIsFormValid(isValid && Object.keys(newErrors).length === 0);
  }, [formData, isEditMode, assets, isCloud]);

  useEffect(() => {
    setInputValue(formData.cnX_WellName);
  }, [formData.cnX_WellName]);

  useEffect(() => {
    if (typeof inputValue === "string" && inputValue.length > 4) {
      const filteredItems = searchResults.filter((result) =>
        `${result.wellName} (${result.apI_UWI})`
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      );
      setSearchDropdownItems(
        filteredItems.map((result, index) => ({
          id: result.id,
          name: result.wellName,
          displayName: result.apI_UWI
            ? `${result.wellName} (${result.apI_UWI})`
            : result.wellName,
          cnX_SiteId: result.siteID,
          envWellID: result.envWellID,
          apI_UWI: result.apI_UWI,
          key: `${result.id}-${index}`,
        }))
      );
    }
  }, [searchResults, inputValue]);

  useEffect(() => {
    const filteredItems = businessUnits.filter((result) =>
      result.name
        .toLowerCase()
        .includes(businessUnitInputValue.toLowerCase())
    );
    setBusinessUnitDropdownItems(
      filteredItems.map((result) => ({
        name: result.name,
      }))
    );
  }, [businessUnits, businessUnitInputValue]);

  useEffect(() => {
    const filteredItems = fields.filter((result) =>
      result.name
        .toLowerCase()
        .includes(fieldInputValue.toLowerCase())
    );
    setFieldDropdownItems(
      filteredItems.map((result) => ({
        name: result.name,
      }))
    );
  }, [fields, fieldInputValue]);

  const openAssetModal = () => setIsAssetModalOpen(true);
  const closeAssetModal = () => {
    setInputValue("");
    setIsAssetModalOpen(false);
    setFormData(initialFormData);
    setErrors({});
    setIsEditMode(false);
  };

  const openAddUserModal = () => {
    setIsEditMode(false);
    setOriginalFormData(initialFormData);
    setFormData(initialFormData);
    openAssetModal();
    setSearchDropdownItems([]);
    setFieldInputValue("");
    setBusinessUnitInputValue("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => {
      let updatedFormData = { ...prevState };

      if (name.startsWith("pumpChecker.")) {
        const pumpCheckerField = name.split(".")[1];
        updatedFormData = {
          ...prevState,
          pumpChecker: {
            ...prevState.pumpChecker,
            [pumpCheckerField]:
              pumpCheckerField === "identifier"
                ? value.trim()
                : type === "checkbox"
                  ? checked
                  : value.replace(/^\s+/, ""),
          },
        };
      } else {
        updatedFormData[name] =
          type === "checkbox" ? checked : value.replace(/^\s+/, "");
      }

      return updatedFormData;
    });
  };

  useEffect(() => {
    if (!formData.portId || !formData.nodeAddress) {
      handleInputChange({
        target: { name: "isEnabled", checked: false, type: "checkbox" },
      });
    }
  }, [formData.portId, formData.nodeAddress]);

  const handleDropdownChange = (item: DropdownItems, field: string) => {
    setFormData((prevState) => {
      const updatedFormData = { ...prevState };

      if (field.startsWith("pumpChecker.")) {
        const pumpCheckerField = field.split(".")[1];
        updatedFormData.pumpChecker = {
          ...prevState.pumpChecker,
          [pumpCheckerField]: item.name,
        };

        if (pumpCheckerField === "timezone") {
          updatedFormData.pumpChecker.timezone = item.id;
        }

        if (!isCloud && pumpCheckerField === "methodOfProduction") {
          updatedFormData.applicationId = item.id;
          updatedFormData.pocType = {
            id: "",
            lookupType: "",
            legacyId: "",
            description: "",
          };

          dispatch(loadAssetPOCTypeByApplication(item.id));
        }
      } else {
        updatedFormData[field] = field === "portId" ? item.id : item.name;
        if (field === "customerName") {
          updatedFormData.customerId = item.id;
        }
      }

      if (field === "type") {
        const pocType = {
          id: item.id,
          lookupType: item.lookupType,
          legacyId: item.legacyId,
          description: item.name,
        };
        return {
          ...updatedFormData,
          pocType,
        };
      }

      return updatedFormData;
    });
  };

  const handleSearchDropdownChange = (selectedAsset: searchDropdownItem) => {
    setFormData((prevState: AssetItem) => ({
      ...prevState,
      cnX_WellId: selectedAsset.id ? String(selectedAsset.id) : null,
      cnX_WellName: selectedAsset.name,
      wellname: !isEditMode ? selectedAsset.name : prevState.name,
      cnX_SiteId: selectedAsset.cnX_SiteId
        ? String(selectedAsset.cnX_SiteId)
        : null,
      envWellId: selectedAsset.envWellID
        ? String(selectedAsset.envWellID)
        : null,
      apI_UWI: selectedAsset.apI_UWI ? String(selectedAsset.apI_UWI) : null,
    }));
    setInputValue(selectedAsset.name);
  };

  const handleMasterDataInputValue = async (value: string, field: string) => {
    const trimmedValue = value.replace(/^\s+/, "");
    setInputValuesForMasterDataElements(field, trimmedValue);
  };


  const handleMasterDataDropdownChange = (selectedItem: searchDropdownItem, field: string) => {
    setInputValuesForMasterDataElements(field, selectedItem.name);
  };

  const setInputValuesForMasterDataElements = (field: string, name: string) => {
    if (field === "businessUnit") {
      setBusinessUnitInputValue(name);
    }
    if (field === "field") {
      setFieldInputValue(name);
    }
    handleInputChange({
      target: { name: 'pumpChecker.' + field, value: name },
    });
  }

  const controllerPortItems = controllerPorts
    .map((port) => ({
      id: port.id,
      name: `${port.serverName}/${port.portID}/${port.description}`,
      portID: Number(port.portID),
    }))
    .sort((a, b) => a.portID - b.portID);

  const controllerTypeItems =
    isCloud || formData.pumpChecker.methodOfProduction
      ? (Array.isArray(controllerTypes) ? controllerTypes : [])
        .map((type) => ({
          id: type.id,
          lookupType: type.lookupType,
          legacyId: type.pocType,
          name: type.description || "",
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      : [];

  const matchedApplications = applicationItems.find(
    (method) => method.id === formData.applicationId
  );

  const matchedControllerType = controllerTypeItems.find(
    (type) => type.id === formData.pocType?.id
  );

  const matchedControllerPort = controllerPortItems.find(
    (port) => port.id === formData.portId
  );

  const matchedTimezone = timezoneItems.find(
    (tz) => tz.id === formData.pumpChecker.timezone
  );

  const handleAssetSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const currentDate = new Date().toISOString();

    const updatedFormData: AssetItem = {
      ...formData,
      name: formData.wellname,
      pocType: {
        ...formData.pocType,
        description: matchedControllerType
          ? matchedControllerType.name
          : formData.pocType.description,
      },
      cnX_SiteId: formData.cnX_SiteId ?? 0,
      cnX_WellId: formData.cnX_WellId ?? 0,
      envWellId: formData.envWellId ?? 0,
      apI_UWI: isCloud
        ? formData.apI_UWI ?? null
        : formData.pumpChecker.identifier,
      createdOnDate: formData.createdOnDate
        ? formData.createdOnDate
        : currentDate,
      lastModifiedDate: currentDate,
      action: isEditMode ? "update" : "insert",
      portId: formData.portId ?? "",

      ...(!isCloud && {
        pumpChecker: {
          ...formData.pumpChecker,
          methodOfProduction: "",
          identifierType: "API",
          latitude: null,
          longitude: null,
          timezone: formData.pumpChecker.timezone ?? "",
        },

        // Exclude top-level duplicates
        field: undefined,
        area: undefined,
        businessUnit: undefined,
        timezone: undefined,
      }),
      wellname: undefined,
    };

    if (Object.keys(errors).length === 0) {
      try {
        const action = isEditMode
          ? editAsset({ assetId: formData.id, updatedAsset: updatedFormData })
          : addAsset(updatedFormData);

        const response = await dispatch(action).unwrap();

        const isSuccessStatus = (status: string) =>
          status === "Success" || status === "true";

        if (Array.isArray(response)) {
          const hasSuccess = response.some((item) =>
            isSuccessStatus(item.status)
          );

          const showToastsSequentially = async () => {
            for (const item of response) {
              setToastMessage(item.message);
              setTextColor(isSuccessStatus(item.status) ? "green" : "red");
              setShowToast(true);

              await new Promise((resolve) => setTimeout(resolve, 3500));

              setShowToast(false);
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          };

          if (hasSuccess) {
            setSaveFailed(false);
            dispatch(loadAsset());
            closeAssetModal();
          } else {
            setSaveFailed(true);
          }

          showToastsSequentially();
        } else {
          const singleResponse = response as ResponseItem;
          setSaveFailed(!isSuccessStatus(singleResponse.status));
          setToastMessage(singleResponse.message);
          setTextColor(
            isSuccessStatus(singleResponse.status) ? "green" : "red"
          );
          setShowToast(true);

          if (isSuccessStatus(singleResponse.status)) {
            dispatch(loadAsset());
            closeAssetModal();
          }
        }
      } catch (errorResp: any) {

        let errorMsg = "";
        if (errorResp.response?.status === 401) {
          errorMsg = "Unauthorized: Customer ID does not exist.";
        } else if (errorResp.response?.status === 404) {
          errorMsg = "Not found: Module or ModuleId is missing for onboarding.";
        } else if (errorResp.response?.status === 500) {
          errorMsg = "Internal server error.";
        } else if (errorResp.response?.status === 400 && errorResp.response?.data?.includes("no valid license")) {
          errorMsg = "License expired: There is no valid license.";
        } else if (errorResp.response?.status === 403 && errorResp.response?.data?.includes("Exceeded")) {
          errorMsg = "Exceeded the allowed well count.";
        }

        setSaveFailed(true);
        setToastMessage(
          errorMsg || "An unexpected error occurred."
        );
        setTextColor("red");
        setShowToast(true);

        if (errorMsg) return;

        dispatch(loadAsset());
        closeAssetModal();
      }
    }
  };

  const addCustomerHandler = (newCustomer: CustomerItem) => {
    dispatch(addCustomer(newCustomer));
  };

  const customNoResultsMessage = (
    <>
      <p className="no-customer-found">Customer not listed?</p>
      <span className="add-new-customer">
        <AddCustomer addCustomer={addCustomerHandler} triggerType="button" />
      </span>
    </>
  );

  const handleInputChangeLogging = async (value: string) => {
    const trimmedValue = value.replace(/^\s+/, "");
    setInputValue(trimmedValue);

    if (trimmedValue.length === 5) {
      dispatch(searchAssetByName(trimmedValue));
    }
  };

  const handleCancelClick = () => {
    if (saveFailed) {
      dispatch(loadAsset());
      setSaveFailed(false);
    }
    if (hasChanges(formData)) {
      setIsConfirmationDialogOpen(true);
    } else {
      handleConfirmCancel();
    }
  };

  const handleConfirmationDialogClose = () =>
    setIsConfirmationDialogOpen(false);

  const handleConfirmCancel = () => {
    setIsConfirmationDialogOpen(false);
    closeAssetModal();
  };

  return (
    <div>
      {isWellAdmin && (
        <Tooltip content="Add new asset" direction="top">
          <span className="add-user-icon" onClick={openAddUserModal}>
            <AddAssetIcon />
          </span>
        </Tooltip>
      )}
      <Modal isOpen={isAssetModalOpen} onClose={closeAssetModal}>
        <div style={{ position: "relative" }}>
          {assetSaveLoading && (
            <div className="spinner-overlay">
              <Loader isOverlay={true} />
            </div>
          )}
          <form
            className="add-user-form"
            onSubmit={handleAssetSubmit}
            onKeyDown={(e) => handleCheckboxKeyDown(e)}
          >
            <div className="form-row">
              <label style={{ fontSize: "20px" }}>
                {isEditMode ? "Edit well" : "Create a new well"}
              </label>
            </div>
            <div className="form-row">
              {isCloud ? (
                <div className="form-group">
                  <label>CNX Name (API UWI Number)</label>
                  <SearchDropdown
                    items={searchDropdownItems}
                    defaultOption={formData.cnX_WellName || "Select a well"}
                    onChange={handleSearchDropdownChange}
                    onInputChange={handleInputChangeLogging}
                    inputValue={inputValue}
                    isLoading={assetSearchLoading}
                    infoMessage="Type 5 or more characters to search"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>
                    <span>Identifier:</span>
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="alert-icon-container">
                    <input
                      type="text"
                      name="pumpChecker.identifier"
                      value={formData.pumpChecker.identifier}
                      onChange={handleInputChange}
                      autoComplete="off"
                      maxLength={300}
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>
                  <span>Well Name</span>
                  <span className="required-asterisk">*</span>
                </label>
                <div className="alert-icon-container">
                  <input
                    type="text"
                    name="wellname"
                    value={formData.wellname}
                    onChange={handleInputChange}
                    autoComplete="off"
                    maxLength={50}
                  />
                  {errors.name && (
                    <span className="error-icon">
                      <Tooltip content={errors.name} direction="top">
                        <AlertIcon />
                      </Tooltip>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div
                className={`form-group ${isCloud ? "asset-customer-field" : "dropdown-form-group"
                  }`}
              >
                <label>
                  <span>Customer Name:</span>
                  <span className="required-asterisk">*</span>
                </label>
                <Dropdown
                  items={customerItems}
                  defaultOption={
                    isCloud
                      ? formData.customerName || "Select customer"
                      : customerItems.length > 0
                        ? customerItems[0].name
                        : "Select customer"
                  }
                  onChange={(item) =>
                    handleDropdownChange(item, "customerName")
                  }
                  disabled={isCloud ? isEditMode : true}
                  noResultsMessage={isCloud ? customNoResultsMessage : <></>}
                  className="cloud-dropdown-width"
                />
              </div>
              {!isCloud && (
                <div className="form-group dropdown-form-group">
                  <label>
                    <span>Method of Production:</span>
                    <span className="required-asterisk">*</span>
                  </label>
                  <Dropdown
                    items={applicationItems}
                    defaultOption={
                      matchedApplications
                        ? matchedApplications.name
                        : "Select method"
                    }
                    onChange={(item) =>
                      handleDropdownChange(
                        item,
                        "pumpChecker.methodOfProduction"
                      )
                    }
                  />
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group dropdown-form-group">
                <label>
                  <span>Controller Type:</span>
                  <span className="required-asterisk">*</span>
                </label>
                <Dropdown
                  key={formData.pocType?.id || "empty"}
                  items={controllerTypeItems}
                  defaultOption={
                    matchedControllerType
                      ? matchedControllerType.name
                      : "Select type"
                  }
                  onChange={(item) => handleDropdownChange(item, "type")}
                />
              </div>
              <div className="form-group dropdown-form-group">
                <label>Controller Port:</label>
                <div className="alert-icon-container">
                  <Dropdown
                    items={controllerPortItems}
                    defaultOption={
                      matchedControllerPort
                        ? matchedControllerPort.name
                        : "Select port"
                    }
                    onChange={(item) => handleDropdownChange(item, "portId")}
                    allowDeselect={true}
                    deselectOption="Select port"
                  />
                  {errors.portAndAddress && (
                    <span className="error-icon">
                      <Tooltip content={errors.portAndAddress} direction="top">
                        <AlertIcon />
                      </Tooltip>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Node Address:</label>
                <div className="alert-icon-container">
                  <input
                    type="text"
                    name="nodeAddress"
                    value={formData.nodeAddress}
                    onChange={handleInputChange}
                    autoComplete="off"
                    style={{ width: isCloud ? "95%" : "" }}
                    maxLength={255}
                  />
                  {errors.portAndAddress && (
                    <span className="error-icon">
                      <Tooltip content={errors.portAndAddress} direction="top">
                        <AlertIcon />
                      </Tooltip>
                    </span>
                  )}
                </div>
              </div>
              {!isCloud && (
                <div className="form-group dropdown-form-group">
                  <label>
                    <span>Timezone:</span>
                  </label>
                  <Dropdown
                    items={timezoneItems}
                    defaultOption={
                      matchedTimezone ? matchedTimezone.name : "Select timezone"
                    }
                    onChange={(item) =>
                      handleDropdownChange(item, "pumpChecker.timezone")
                    }
                    allowDeselect={true}
                    deselectOption="Select timezone"
                  />
                </div>
              )}
            </div>
            {!isCloud && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <span>Business Unit:
                        {(formData.pumpChecker.area ||
                          formData.pumpChecker.field) &&
                          !formData.pumpChecker.businessUnit && (
                            <span className="required-asterisk">*</span>
                          )}
                      </span>
                    </label>
                    <div className="alert-icon-container">
                      <MasterSearchDropdown
                        items={businessUnitDropdownItems}
                        defaultOption={formData.pumpChecker.businessUnit || "Search a Business Unit"}
                        onChange={(item) => handleMasterDataDropdownChange(item, "businessUnit")}
                        onInputChange={(item) => handleMasterDataInputValue(item, "businessUnit")}
                        inputValue={businessUnitInputValue}
                        disabled={false}
                        isLoading={masterDataLoading === "loading"}
                        maxLength={200}
                      />
                      {(errors.fieldDependency || errors.areaDependency) && (
                        <span className="error-icon">
                          <Tooltip
                            content={
                              errors.fieldDependency || errors.areaDependency
                            }
                            direction="top"
                          >
                            <AlertIcon />
                          </Tooltip>
                        </span>
                      )}

                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      <span>Field:</span>
                    </label>
                    <MasterSearchDropdown
                      items={fieldDropdownItems}
                      defaultOption={formData.pumpChecker.field || "Search a Field"}
                      onChange={(item) => handleMasterDataDropdownChange(item, "field")}
                      onInputChange={(item) => handleMasterDataInputValue(item, "field")}
                      inputValue={fieldInputValue}
                      disabled={false}
                      isLoading={masterDataLoading === "loading"}
                      maxLength={200}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="form-row">
              {!isCloud && (
                <div className="form-group">
                  <label>
                    <span>Area:</span>
                    {formData.pumpChecker.field &&
                      !formData.pumpChecker.area && (
                        <span className="required-asterisk">*</span>
                      )}
                  </label>
                  <div className="alert-icon-container">
                    <input
                      type="text"
                      name="pumpChecker.area"
                      value={formData.pumpChecker.area}
                      onChange={handleInputChange}
                      autoComplete="off"
                      maxLength={300}
                    />
                    {errors.fieldDependency && (
                      <span className="error-icon">
                        <Tooltip
                          content={errors.fieldDependency}
                          direction="top"
                        >
                          <AlertIcon />
                        </Tooltip>
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div
                className="form-group form-group-inline"
                style={{ marginTop: "10px" }}
              >
                <Tooltip
                  content="Controller Port and Node Address are required to enable the well."
                  direction="top"
                  visible={
                    !formData.portId || !formData.nodeAddress ? true : false
                  }
                >
                  <input
                    type="checkbox"
                    id="isEnabled"
                    name="isEnabled"
                    checked={formData.isEnabled}
                    onChange={handleInputChange}
                    disabled={!formData.portId || !formData.nodeAddress}
                    style={{ width: "15px" }}
                    className="settings-checkbox-input"
                  />
                </Tooltip>
                <label htmlFor="isEnabled">Is Enabled</label>
              </div>
            </div>
            <div className="form-row">
              <p className="mandatory-fields-message">
                Fields marked with<span className="required-asterisk">*</span>{" "}
                are mandatory.
              </p>
            </div>
            <div
              className="form-row"
              style={{ gap: "10px", marginTop: "-15px", marginBottom: "-8px" }}
            >
              <button
                type="submit"
                className="add-user-button"
                disabled={
                  isEditMode
                    ? !isFormValid || !hasChanges(formData)
                    : !isFormValid
                }
              >
                {isEditMode ? "Save" : "Add Well"}
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Modal>
      {showToast && (
        <SuccessfullToast
          message={toastMessage}
          isShowEnabled={showToast}
          setShow={setShowToast}
          colorText={textcolor}
        />
      )}
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        onConfirm={handleConfirmCancel}
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
      />
    </div>
  );
};

export default AddAssetDetails;
